import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type {
  PartnerCommissionRow,
  PartnerCommissionStatus,
  PartnerPaymentHistoryRow,
  PartnerPaymentsSummary,
} from '@/types'

export type PaymentsFilters = {
  parceiroId: string
  status: '' | PartnerCommissionStatus
  fromMonth: string
  toMonth: string
}

export type BatchPaymentForm = {
  metodo: string
  dataPagamento: string
  notas: string
}

const PAGE_SIZE = 50

/** Competências recorrentes são gravadas no dia 1 às 00:00 UTC — os limites do mês precisam ser em UTC. */
function monthRange(fromMonth: string, toMonth: string) {
  const params: Record<string, string> = {}
  if (fromMonth) {
    const [y, m] = fromMonth.split('-').map(Number)
    params.from = new Date(Date.UTC(y, m - 1, 1)).toISOString()
  }
  if (toMonth) {
    const [y, m] = toMonth.split('-').map(Number)
    params.to = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)).toISOString()
  }
  return params
}

export function usePartnerPayments(version: number, onChanged?: () => Promise<void> | void) {
  const [filters, setFilters] = useState<PaymentsFilters>({ parceiroId: '', status: '', fromMonth: '', toMonth: '' })
  const [page, setPage] = useState(1)
  const [summary, setSummary] = useState<PartnerPaymentsSummary | null>(null)
  const [commissions, setCommissions] = useState<PartnerCommissionRow[]>([])
  const [commissionsMeta, setCommissionsMeta] = useState({ totalItems: 0, totalPages: 1, totalValor: 0 })
  const [history, setHistory] = useState<PartnerPaymentHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const afterChange = useCallback(async () => {
    if (!onChanged) {
      setReloadKey((k) => k + 1)
      return
    }
    try {
      await onChanged()
    } catch {
      setReloadKey((k) => k + 1)
    }
  }, [onChanged])

  useEffect(() => {
    setPage(1)
  }, [filters])

  useEffect(() => {
    const controller = new AbortController()
    const range = monthRange(filters.fromMonth, filters.toMonth)
    const commonParams = { ...(filters.parceiroId ? { parceiroId: filters.parceiroId } : {}), ...range }

    setLoading(true)
    Promise.all([
      api.get('/parceiros/payments/summary', { signal: controller.signal }),
      api.get('/parceiros/payments/commissions', {
        signal: controller.signal,
        params: { ...commonParams, ...(filters.status ? { status: filters.status } : {}), page, limit: PAGE_SIZE },
      }),
      api.get('/parceiros/payments/history', { signal: controller.signal, params: commonParams }),
    ])
      .then(([summaryResp, commissionsResp, historyResp]) => {
        setSummary(summaryResp.data.data)
        setCommissions(commissionsResp.data.data ?? [])
        setCommissionsMeta({
          totalItems: commissionsResp.data.meta?.totalItems ?? 0,
          totalPages: Math.max(1, commissionsResp.data.meta?.totalPages ?? 1),
          totalValor: commissionsResp.data.meta?.totalValor ?? 0,
        })
        setHistory(historyResp.data.data ?? [])
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) toast.error(getFriendlyError(err, 'Erro ao carregar pagamentos de parceiros'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [filters, page, version, reloadKey])

  const updateStatus = async (ids: string[], status: 'pendente' | 'aprovada') => {
    if (ids.length === 0) return
    setBusy(true)
    try {
      const resp = await api.post('/parceiros/payments/commissions/status', { ids, status })
      toast.success(
        status === 'aprovada'
          ? `${resp.data.data?.updated ?? ids.length} comissão(ões) aprovada(s)`
          : 'Comissões voltaram para pendente',
      )
      void afterChange()
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao atualizar comissões'))
    } finally {
      setBusy(false)
    }
  }

  const registerPayment = async (commissionIds: string[], form: BatchPaymentForm) => {
    setBusy(true)
    try {
      const resp = await api.post('/parceiros/payments/batch', {
        commissionIds,
        metodo: form.metodo,
        notas: form.notas,
        dataPagamento: form.dataPagamento ? new Date(`${form.dataPagamento}T12:00:00`).toISOString() : undefined,
      })
      const pagamentos = resp.data.data?.pagamentos?.length ?? 0
      toast.success(`${pagamentos} pagamento(s) registrado(s)`)
      void afterChange()
      return true
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao registrar pagamento'))
      return false
    } finally {
      setBusy(false)
    }
  }

  return {
    filters,
    setFilters,
    page,
    setPage,
    summary,
    commissions,
    commissionsMeta,
    history,
    loading,
    busy,
    updateStatus,
    registerPayment,
  }
}
